// SPDX-License-Identifier: AGPL-3.0-or-later

import type {GuildReadyData} from '@app/features/gateway/types/GatewayGuildTypes';
import type {VoiceState} from '@app/features/gateway/types/GatewayVoiceTypes';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {
	createVoiceGatewayStateSnapshot,
	transitionVoiceGatewayStateSnapshot,
	type VoiceGatewayStateContext,
	type VoiceGatewayStateEvent,
	type VoiceGatewayStateSnapshot,
} from '@app/features/voice/engine/VoiceGatewayStateMachine';
import {runInAction} from 'mobx';

const logger = new Logger('VoiceStateGatewayHandler');

export class VoiceStateGatewayHandler {
	private snapshot: VoiceGatewayStateSnapshot;

	constructor(private applyContext: (context: VoiceGatewayStateContext) => void) {
		this.snapshot = createVoiceGatewayStateSnapshot();
		this.publishContext();
	}

	handleGatewayVoiceStateUpdate(guildId: string | null, voiceState: VoiceState): void {
		if (!voiceState.connection_id) {
			logger.warn('Voice state missing connection_id:', voiceState);
			return;
		}
		this.send({type: 'voiceState.update', guildId, voiceState});
		if (voiceState.channel_id) {
			logger.debug('Voice state updated', {
				guildId,
				channelId: voiceState.channel_id,
				userId: voiceState.user_id,
				connectionId: voiceState.connection_id,
				selfMute: voiceState.self_mute,
				selfDeaf: voiceState.self_deaf,
				selfVideo: voiceState.self_video,
				selfStream: voiceState.self_stream,
				viewerStreamKeys: voiceState.viewer_stream_keys,
			});
			return;
		}
		logger.debug('User left voice channel', {
			guildId,
			userId: voiceState.user_id,
			connectionId: voiceState.connection_id,
		});
	}

	handleGatewayVoiceStateDelete(guildId: string, userId: string): void {
		this.send({type: 'voiceState.delete', guildId, userId});
		logger.debug('Voice states deleted for user in guild', {guildId, userId});
	}

	handleConnectionOpen(guilds: Array<GuildReadyData>): void {
		this.send({type: 'connection.open', guilds});
		logger.info('Initialized voice states from connection open', {
			guildCount: guilds.length,
			totalVoiceStates: Object.keys(this.snapshot.context.connectionVoiceStates).length,
		});
	}

	handleGuildCreate(guild: GuildReadyData): void {
		this.send({type: 'guild.create', guild});
		logger.debug('Guild voice states loaded', {
			guildId: guild.id,
			voiceStateCount: guild.voice_states?.length ?? 0,
		});
	}

	handleGuildDelete(guildId: string): void {
		this.send({type: 'guild.delete', guildId});
		logger.info('Removed all voice states for deleted guild', {guildId});
	}

	clearAllVoiceStates(): void {
		this.send({type: 'clear.all'});
	}

	private send(event: VoiceGatewayStateEvent): void {
		this.snapshot = transitionVoiceGatewayStateSnapshot(this.snapshot, event);
		this.publishContext();
	}

	private publishContext(): void {
		runInAction(() => {
			this.applyContext(this.snapshot.context);
		});
	}
}
